"use client";

import * as React from "react";
import { useId } from "react";
import { useVirtualizer, type VirtualItem } from "@tanstack/react-virtual";
import { cn } from "../utils/cn";
import { useSmartTranslations } from "../hooks/useSmartTranslations";
import { useGlobalI18n } from "../contexts/GlobalI18nContext";
import { ChevronDown, Search, Check, SearchX, Loader2, X, Sparkles } from "lucide-react";
import { useShadCNAnimations } from "../utils/animations";
import { Popover } from "./Popover";
import { useOverlayScrollbarTarget } from "./OverlayScrollbarProvider";
import { getBorderRadiusClass, type BorderMode } from "../utils/radius";

export interface MultiComboboxOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
  description?: string;
  disabled?: boolean;
  group?: string;
}

/** Public props for the `MultiCombobox` component. */
export interface MultiComboboxProps {
  id?: string;
  options: Array<string | MultiComboboxOption>;
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  maxSelected?: number;
  disabledOptions?: string[];
  showTags?: boolean;
  showClear?: boolean;
  className?: string;
  disabled?: boolean;
  size?: "sm" | "md" | "lg";
  variant?: "default" | "outline" | "ghost";
  label?: string;
  /** Custom class for label */
  labelClassName?: string;
  title?: string;
  required?: boolean;
  displayFormat?: (option: MultiComboboxOption) => string;
  loading?: boolean;
  loadingText?: string;
  emptyText?: string;
  // New props
  borderMode?: BorderMode;
  showSelectedIcons?: boolean;
  maxHeight?: number;
  groupBy?: (option: MultiComboboxOption) => string;
  renderOption?: (option: MultiComboboxOption, isSelected: boolean) => React.ReactNode;
  renderTag?: (option: MultiComboboxOption, onRemove: () => void) => React.ReactNode;
  /** Selected option fallbacks for manual/server search when options does not contain current values. */
  selectedOptions?: MultiComboboxOption[];
  error?: string;
  helperText?: string;
  maxTagsVisible?: number;
  /** Enable OverlayScrollbars on dropdown options list. Default: true when not virtualized */
  useOverlayScrollbar?: boolean;
  /** Virtualize large flat option lists. Grouped lists fall back to normal rendering. Default: false */
  virtualized?: boolean;
  /** Estimated option row height used by virtualized rendering. Default: 44 */
  estimatedItemHeight?: number;
  /** Number of extra rows rendered above and below the visible range. Default: 8 */
  overscan?: number;
  /** Use "manual" to let callers provide server-filtered options via onSearchChange. Default: "auto" */
  searchMode?: "auto" | "manual";
  /** Called whenever the search query changes. Required for manual/server search. */
  onSearchChange?: (query: string) => void;
  /** Debounce delay for onSearchChange. Default: 0 */
  searchDebounceMs?: number;
  /** Minimum query length before showing options in manual/search-prompt mode. Default: 0 */
  minSearchLength?: number;
  /** Limit the number of rendered options before the user types a query. */
  maxInitialOptions?: number;
  /** Show a prompt instead of options while the query is shorter than minSearchLength. Default: false */
  showSearchPromptWhenEmptyQuery?: boolean;
}

const comboboxScrollClassName = [
  "scrollbar-thin",
  "[scrollbar-width:thin]",
  "[scrollbar-color:color-mix(in_oklch,var(--muted-foreground)_28%,transparent)_transparent]",
  "[&::-webkit-scrollbar]:w-2",
  "[&::-webkit-scrollbar-track]:bg-transparent",
  "[&::-webkit-scrollbar-thumb]:rounded-full",
  "[&::-webkit-scrollbar-thumb]:border-2",
  "[&::-webkit-scrollbar-thumb]:border-solid",
  "[&::-webkit-scrollbar-thumb]:border-transparent",
  "[&::-webkit-scrollbar-thumb]:bg-clip-padding",
  "[&::-webkit-scrollbar-thumb]:bg-muted-foreground/25",
  "[&::-webkit-scrollbar-thumb:hover]:bg-muted-foreground/45",
].join(" ");

export const MultiCombobox: React.FC<MultiComboboxProps> = ({
  id,
  options,
  value,
  onChange,
  placeholder: placeholderProp,
  searchPlaceholder: searchPlaceholderProp,
  maxSelected,
  disabledOptions = [],
  showTags = true,
  showClear = true,
  className,
  disabled = false,
  size = "md",
  variant = "default",
  label,
  labelClassName,
  title,
  required,
  displayFormat = (option) => option.label,
  loading = false,
  loadingText: loadingTextProp,
  emptyText: emptyTextProp,
  borderMode,
  showSelectedIcons = true,
  maxHeight = 280,
  groupBy,
  renderOption,
  renderTag,
  selectedOptions: selectedOptionsProp,
  error,
  helperText,
  maxTagsVisible = 3,
  useOverlayScrollbar = true,
  virtualized = false,
  estimatedItemHeight = 44,
  overscan = 8,
  searchMode = "auto",
  onSearchChange,
  searchDebounceMs = 0,
  minSearchLength = 0,
  maxInitialOptions,
  showSearchPromptWhenEmptyQuery = false,
}) => {
  const tv = useSmartTranslations("ValidationInput");
  const gi18n = useGlobalI18n();
  const placeholder = placeholderProp ?? gi18n.selectPlaceholder ?? "Select...";
  const searchPlaceholder = searchPlaceholderProp ?? gi18n.searchPlaceholder ?? "Search...";
  const loadingText = loadingTextProp ?? gi18n.loading ?? "Loading...";
  const emptyText = emptyTextProp ?? gi18n.noResults ?? "No results found";
  const [query, setQuery] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
  const [localRequiredError, setLocalRequiredError] = React.useState<string | undefined>();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<Array<HTMLElement | null>>([]);
  const optionsListRef = React.useRef<HTMLUListElement>(null);

  useOverlayScrollbarTarget(optionsListRef, { enabled: open && useOverlayScrollbar && !virtualized });

  const triggerRef = React.useRef<HTMLButtonElement>(null);

  // Inject ShadCN animations
  useShadCNAnimations();

  // Normalize options to objects with all properties
  const normalizedOptions = React.useMemo<MultiComboboxOption[]>(
    () =>
      options.map((o) =>
        typeof o === "string"
          ? { value: o, label: o }
          : { value: o.value, label: o.label, icon: o.icon, description: o.description, disabled: o.disabled, group: o.group },
      ),
    [options],
  );

  // Enable search only when useful, or when controlled by server/manual mode.
  const enableSearch = normalizedOptions.length > 10 || searchMode === "manual" || minSearchLength > 0 || !!onSearchChange;
  const trimmedQuery = query.trim();
  const queryMeetsMinimum = trimmedQuery.length >= minSearchLength;
  const shouldPromptForSearch = minSearchLength > 0 && !queryMeetsMinimum && (searchMode === "manual" || showSearchPromptWhenEmptyQuery);

  const filtered = React.useMemo(() => {
    if (shouldPromptForSearch) return [];
    if (!enableSearch || searchMode === "manual") return normalizedOptions;

    const normalizedQuery = trimmedQuery.toLowerCase();
    if (!normalizedQuery) return normalizedOptions;
    return normalizedOptions.filter(
      (opt) => opt.label.toLowerCase().includes(normalizedQuery) || opt.description?.toLowerCase().includes(normalizedQuery),
    );
  }, [enableSearch, normalizedOptions, searchMode, shouldPromptForSearch, trimmedQuery]);

  const renderLimitedOptions = React.useMemo(() => {
    if (trimmedQuery || maxInitialOptions === undefined || maxInitialOptions < 1) {
      return filtered;
    }
    return filtered.slice(0, maxInitialOptions);
  }, [filtered, maxInitialOptions, trimmedQuery]);

  const canVirtualize = virtualized && !groupBy;
  const optionVirtualizer = useVirtualizer({
    count: canVirtualize ? renderLimitedOptions.length : 0,
    getScrollElement: () => optionsListRef.current,
    estimateSize: () => estimatedItemHeight,
    initialRect: { width: 0, height: maxHeight },
    overscan,
    enabled: canVirtualize,
  });
  const virtualItems = canVirtualize ? optionVirtualizer.getVirtualItems() : [];

  const scrollVirtualListToIndex = React.useCallback((index: number) => {
    if (!canVirtualize || renderLimitedOptions.length === 0) return;
    optionVirtualizer.scrollToIndex(index, { align: "auto" });
  }, [canVirtualize, optionVirtualizer, renderLimitedOptions.length]);

  const scrollVirtualListToStart = React.useCallback(() => {
    scrollVirtualListToIndex(0);
  }, [scrollVirtualListToIndex]);

  // Group options if groupBy is provided
  const groupedOptions = React.useMemo(() => {
    if (!groupBy) return null;
    const groups = new Map<string, MultiComboboxOption[]>();
    renderLimitedOptions.forEach((opt) => {
      const group = groupBy(opt);
      if (!groups.has(group)) groups.set(group, []);
      groups.get(group)!.push(opt);
    });
    return groups;
  }, [renderLimitedOptions, groupBy]);

  const toggleSelect = (optionValue: string) => {
    const option = normalizedOptions.find((o) => o.value === optionValue);
    if (option?.disabled || disabledOptions.includes(optionValue)) return;
    if (value.includes(optionValue)) {
      onChange(value.filter((v) => v !== optionValue));
    } else {
      if (!maxSelected || value.length < maxSelected) {
        setLocalRequiredError(undefined);
        onChange([...value, optionValue]);
      }
    }
  };

  const handleRemove = (val: string) => {
    onChange(value.filter((v) => v !== val));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) setOpen(true);

    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (renderLimitedOptions.length === 0) return;
      const next = activeIndex === null ? 0 : (activeIndex + 1) % renderLimitedOptions.length;
      setActiveIndex(next);
      scrollVirtualListToIndex(next);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (renderLimitedOptions.length === 0) return;
      const next = activeIndex === null
        ? renderLimitedOptions.length - 1
        : (activeIndex - 1 + renderLimitedOptions.length) % renderLimitedOptions.length;
      setActiveIndex(next);
      scrollVirtualListToIndex(next);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex !== null && renderLimitedOptions[activeIndex]) {
        toggleSelect(renderLimitedOptions[activeIndex].value);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const effectiveError = error ?? localRequiredError;

  React.useEffect(() => {
    if (disabled || !required || value.length > 0) {
      setLocalRequiredError(undefined);
    }
  }, [disabled, required, value.length]);

  // Auto-focus input when dropdown opens (only if search is enabled)
  React.useEffect(() => {
    if (open && enableSearch) {
      // Focus input after dropdown is positioned
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    } else if (!open) {
      setQuery("");
      setActiveIndex(null);
      scrollVirtualListToStart();
    }
  }, [enableSearch, open, scrollVirtualListToStart]);

  React.useEffect(() => {
    if (!onSearchChange) return undefined;
    const timeoutId = window.setTimeout(() => onSearchChange(query), searchDebounceMs);
    return () => window.clearTimeout(timeoutId);
  }, [onSearchChange, query, searchDebounceMs]);

  React.useEffect(() => {
    if (
      process.env.NODE_ENV !== "production" &&
      normalizedOptions.length > 300 &&
      !virtualized &&
      searchMode !== "manual" &&
      maxInitialOptions === undefined
    ) {
      console.warn(
        '[Underverse UI] MultiCombobox received more than 300 options without virtualization, manual search, or maxInitialOptions. Use virtualized, searchMode="manual", or maxInitialOptions to avoid rendering a large dropdown.',
      );
    }
  }, [maxInitialOptions, normalizedOptions.length, searchMode, virtualized]);

  // Size styles to align with Input defaults
  const sizeStyles = {
    sm: {
      trigger: "h-8 px-3 py-1.5 text-sm md:h-7 md:text-xs",
      icon: "h-4 w-4",
      search: "px-8 py-1.5 text-xs",
      item: "text-xs px-3 py-1.5",
      tag: "px-2 py-0.5 text-[10px]",
    },
    md: {
      trigger: "h-10 px-4 py-2 text-sm",
      icon: "h-4 w-4",
      search: "px-10 py-2 text-sm",
      item: "text-sm px-3 py-2",
      tag: "px-2 py-1 text-xs",
    },
    lg: {
      trigger: "h-12 px-5 py-3 text-base",
      icon: "h-5 w-5",
      search: "px-10 py-3 text-base",
      item: "text-base px-3 py-3",
      tag: "px-2.5 py-1 text-sm",
    },
  } as const;

  const variantStyles = {
    default: "border border-input bg-background shadow-sm hover:border-primary/50",
    outline: "border-2 border-input bg-transparent hover:border-primary",
    ghost: "border border-transparent bg-muted/50 hover:bg-muted",
  } as const;

  const autoId = useId();
  const resolvedId = id ? String(id) : `multicombobox-${autoId}`;
  const labelId = label ? `${resolvedId}-label` : undefined;
  const labelSize = size === "sm" ? "text-xs" : size === "lg" ? "text-base" : "text-sm";
  const listboxId = `${resolvedId}-listbox`;

  // Render a single option
  const renderOptionItem = (item: MultiComboboxOption, index: number, virtualItem?: VirtualItem) => {
    const isSelected = value.includes(item.value);
    const isDisabled = item.disabled || disabledOptions.includes(item.value);
    const optionIcon = item.icon;
    const optionDesc = item.description;
    const itemStyle = {
      animationDelay: open ? `${Math.min(index * 20, 200)}ms` : "0ms",
      ...(virtualItem
        ? {
            position: "absolute" as const,
            top: 0,
            left: 0,
            width: "100%",
            transform: `translateY(${virtualItem.start}px)`,
          }
        : {}),
    };
    const measureRef = virtualItem ? optionVirtualizer.measureElement : undefined;

    if (renderOption) {
      return (
        <li
          key={item.value}
          ref={(node) => {
            measureRef?.(node);
            listRef.current[index] = node;
          }}
          data-index={virtualItem?.index}
          style={itemStyle}
          className={cn(virtualItem && "list-none")}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!isDisabled) toggleSelect(item.value);
            inputRef.current?.focus();
          }}
        >
          <div className={cn("dropdown-item", isDisabled && "opacity-50 cursor-not-allowed pointer-events-none")}>
            {renderOption(item, isSelected)}
          </div>
        </li>
      );
    }

    return (
      <li
        key={item.value}
        ref={(node) => {
          measureRef?.(node);
          listRef.current[index] = node;
        }}
        data-index={virtualItem?.index}
        style={itemStyle}
        className={cn(virtualItem && "list-none")}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!isDisabled) toggleSelect(item.value);
          inputRef.current?.focus();
        }}
      >
        <div
          className={cn(
            "dropdown-item flex cursor-pointer items-center gap-3 rounded-full transition-all duration-200",
            sizeStyles[size].item,
            "hover:bg-accent/70 hover:shadow-sm",
            index === activeIndex && "bg-accent/60",
            isSelected && "bg-primary/10 text-primary font-medium",
            isDisabled && "opacity-40 cursor-not-allowed pointer-events-none",
          )}
        >
          {/* Checkbox indicator */}
          <span
            className={cn(
              "shrink-0 w-5 h-5 flex items-center justify-center rounded-md border-2 transition-all duration-200",
              isSelected ? "bg-primary border-primary text-primary-foreground" : "border-muted-foreground/30 bg-transparent",
            )}
          >
            {isSelected && <Check className="w-3 h-3" />}
          </span>

          {/* Icon if exists */}
          {optionIcon && <span className="shrink-0 w-5 h-5 flex items-center justify-center text-muted-foreground">{optionIcon}</span>}

          {/* Label and description */}
          <div className="flex-1 min-w-0">
            <div className={cn("truncate", isSelected && "font-medium text-primary")}>{item.label}</div>
            {optionDesc && <div className="text-xs text-muted-foreground truncate mt-0.5">{optionDesc}</div>}
          </div>
        </div>
      </li>
    );
  };

  const dropdownBody = (
    <div data-combobox-dropdown data-state={open ? "open" : "closed"} className="w-full rounded-2xl md:rounded-3xl overflow-hidden">
      {/* Header with selected count and clear */}
      {value.length > 0 && (
        <div className="px-3 py-2 border-b border-border/40 flex items-center justify-between bg-muted/30">
          <span className="text-xs font-medium text-muted-foreground">
            {maxSelected
              ? (gi18n.selectedCountWithMax
                ? gi18n.selectedCountWithMax(value.length, maxSelected)
                : `${value.length} / ${maxSelected} max`)
              : (gi18n.itemSelectedCount
                ? gi18n.itemSelectedCount(value.length)
                : `${value.length} selected`)}
          </span>
          {showClear && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleClearAll();
              }}
              className="text-xs text-muted-foreground hover:text-destructive transition-colors cursor-pointer flex items-center gap-1"
            >
              <X className="w-3 h-3" />
              {gi18n.clearAll ?? "Clear all"}
            </button>
          )}
        </div>
      )}

      {/* Search input */}
      {enableSearch && (
        <div className="relative border-b border-border/40">
          <Search className={cn("absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground", sizeStyles[size].icon)} />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIndex(null);
              scrollVirtualListToStart();
            }}
            onKeyDown={handleKeyDown}
            placeholder={searchPlaceholder}
            className={cn("w-full bg-transparent focus:outline-none cursor-text placeholder:text-muted-foreground/60", sizeStyles[size].search)}
          />
          {query && (
            <button
              type="button"
              onClick={() => {
                setQuery("");
                scrollVirtualListToStart();
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {/* Options list */}
      <ul
        id={listboxId}
        role="listbox"
        aria-multiselectable="true"
        ref={optionsListRef}
        data-os-ignore={virtualized ? "" : undefined}
        style={{ maxHeight }}
        className={cn(
          "overflow-y-auto p-1.5",
          (!useOverlayScrollbar || virtualized) && comboboxScrollClassName,
          size === "lg" ? "text-base" : size === "sm" ? "text-xs" : "text-sm",
        )}
      >
        {loading ? (
          <li className="px-3 py-8 text-center">
            <div className="flex flex-col items-center gap-3 animate-in fade-in-0 zoom-in-95 duration-300">
              <div className="relative">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <Sparkles className="h-4 w-4 text-primary/60 absolute -top-1 -right-1 animate-pulse" />
              </div>
              <span className="text-muted-foreground font-medium">{loadingText}</span>
            </div>
          </li>
        ) : shouldPromptForSearch ? (
          <li className="px-3 py-8 text-center text-muted-foreground">
            <div className="flex flex-col items-center gap-3 animate-in fade-in-0 zoom-in-95 duration-300">
              <Search className="h-10 w-10 opacity-30 text-muted-foreground" />
              <span className="font-medium block text-foreground">Type at least {minSearchLength} characters to search</span>
            </div>
          </li>
        ) : renderLimitedOptions.length ? (
          groupedOptions ? (
            // Render grouped options
            Array.from(groupedOptions.entries()).map(([group, items]) => (
              <li key={group} className="mb-2">
                <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider sticky top-0 bg-popover/95 backdrop-blur-sm">
                  {group}
                </div>
                <ul>{items.map((item) => renderOptionItem(item, renderLimitedOptions.indexOf(item)))}</ul>
              </li>
            ))
          ) : (
            // Render flat options
            canVirtualize ? (
              <li role="presentation" className="list-none p-0">
                <ul className="relative" style={{ height: `${optionVirtualizer.getTotalSize()}px` }}>
                  {virtualItems.map((virtualItem) => renderOptionItem(renderLimitedOptions[virtualItem.index], virtualItem.index, virtualItem))}
                </ul>
              </li>
            ) : (
              renderLimitedOptions.map((item, index) => renderOptionItem(item, index))
            )
          )
        ) : (
          <li className={cn("px-3 py-8 text-center text-muted-foreground")}>
            <div className="flex flex-col items-center gap-3 animate-in fade-in-0 zoom-in-95 duration-300">
              <SearchX className="h-10 w-10 opacity-30 text-muted-foreground" />
              <div className="space-y-1">
                <span className="font-medium block">{emptyText}</span>
                {query && <span className="text-xs opacity-60">Try a different search term</span>}
              </div>
              {query && (
                <button
                  type="button"
                  onClick={() => {
                    setQuery("");
                    scrollVirtualListToStart();
                  }}
                  className="text-xs text-primary hover:underline flex items-center gap-1"
                >
                  <X className="w-3 h-3" />
                  Clear search
                </button>
              )}
            </div>
          </li>
        )}
      </ul>
    </div>
  );

  // Selected options for rendering tags
  const selectedOptionFallbackMap = React.useMemo(
    () => new Map((selectedOptionsProp ?? []).map((option) => [option.value, option])),
    [selectedOptionsProp],
  );
  const selectedOptions = value
    .map((v) => normalizedOptions.find((o) => o.value === v) ?? selectedOptionFallbackMap.get(v))
    .filter(Boolean) as MultiComboboxOption[];
  const visibleTags = maxTagsVisible ? selectedOptions.slice(0, maxTagsVisible) : selectedOptions;
  const hiddenCount = maxTagsVisible ? Math.max(0, selectedOptions.length - maxTagsVisible) : 0;

  const triggerButton = (
    <button
      ref={triggerRef}
      type="button"
      disabled={disabled}
      id={resolvedId}
      aria-labelledby={labelId}
      role="combobox"
      aria-haspopup="listbox"
      aria-expanded={open}
      aria-controls={listboxId}
      aria-required={required}
      aria-invalid={!!effectiveError}
      className={cn(
        "group flex w-full items-center gap-2 transition-all duration-200",
        getBorderRadiusClass(borderMode ?? "full"),
        sizeStyles[size].trigger,
        variantStyles[variant],
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:border-primary",
        "disabled:cursor-not-allowed disabled:opacity-50",
        open && "ring-2 ring-primary/20 border-primary",
        !!effectiveError && "border-destructive focus-visible:ring-destructive/30",
      )}
    >
      <div className={cn("flex items-center gap-1.5 flex-1 overflow-hidden", size === "sm" ? "min-h-4" : size === "lg" ? "min-h-8" : "min-h-6")}>
        {value.length > 0 ? (
          showTags ? (
            <>
              {visibleTags.map((option) => {
                if (renderTag) {
                  return <React.Fragment key={option.value}>{renderTag(option, () => handleRemove(option.value))}</React.Fragment>;
                }
                return (
                  <span
                    key={option.value}
                    className={cn(
                      "inline-flex items-center gap-1.5 bg-primary/10 text-primary rounded-full transition-all duration-200 min-w-0",
                      "hover:bg-primary/20",
                      sizeStyles[size].tag,
                    )}
                  >
                    {showSelectedIcons && option.icon && <span className="shrink-0 w-3.5 h-3.5 flex items-center justify-center">{option.icon}</span>}
                    <span className="truncate max-w-24">{displayFormat(option)}</span>
                    <span
                      role="button"
                      tabIndex={0}
                      aria-label={`Remove ${displayFormat(option)}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleRemove(option.value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          e.stopPropagation();
                          handleRemove(option.value);
                        }
                      }}
                      className="hover:text-destructive transition-colors cursor-pointer select-none hover:bg-destructive/10 rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </span>
                  </span>
                );
              })}
              {hiddenCount > 0 && (
                <span className={cn("inline-flex items-center bg-muted text-muted-foreground rounded-lg shrink-0", sizeStyles[size].tag)}>
                  {gi18n.moreCount ? gi18n.moreCount(hiddenCount) : `+${hiddenCount} more`}
                </span>
              )}
            </>
          ) : (
            <span className="truncate text-sm font-medium">
              {gi18n.selectedCount
                ? gi18n.selectedCount(value.length)
                : `${value.length} item${value.length > 1 ? "s" : ""} selected`}
            </span>
          )
        ) : (
          <span className="text-muted-foreground">{placeholder}</span>
        )}
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        {showClear && value.length > 0 && (
          <div
            role="button"
            tabIndex={0}
            aria-label={gi18n.clearAll ?? "Clear all"}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleClearAll();
            }}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && handleClearAll()}
            className="opacity-0 group-hover:opacity-100 transition-all duration-200 p-1 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
          >
            <X className="h-3.5 w-3.5" />
          </div>
        )}
        <ChevronDown
          className={cn(
            "h-4 w-4 text-muted-foreground transition-all duration-300 ease-out",
            open && "rotate-180 text-primary",
            !open && "group-hover:text-foreground",
          )}
        />
      </div>
    </button>
  );

  return (
    <div className={cn("w-full space-y-2 group", className)}>
      {/* Title */}
      {title && (
        <div className="flex items-center justify-between">
          <label
            className={cn(
              size === "sm" ? "text-xs" : size === "lg" ? "text-base" : "text-sm",
              "font-medium transition-colors duration-200",
              disabled ? "text-muted-foreground" : "text-foreground group-focus-within:text-primary",
              effectiveError && "text-destructive",
              labelClassName,
            )}
          >
            {title}
            {required && <span className="text-destructive ml-1">*</span>}
          </label>
        </div>
      )}

      {/* Label */}
      {label && (
        <label
          id={labelId}
          onClick={() => triggerRef.current?.focus()}
          className={cn(
            labelSize,
            "font-medium transition-colors duration-200",
            disabled ? "text-muted-foreground" : "text-foreground group-focus-within:text-primary",
            effectiveError && "text-destructive",
            labelClassName,
          )}
        >
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>
      )}
      <input
        tabIndex={-1}
        aria-hidden="true"
        value={value.length > 0 ? "selected" : ""}
        onChange={() => {}}
        required={required}
        disabled={disabled}
        onInvalid={(e) => {
          e.preventDefault();
          setLocalRequiredError(tv("required"));
        }}
        className="pointer-events-none absolute h-0 w-0 opacity-0"
      />

      <Popover
        open={open}
        onOpenChange={setOpen}
        trigger={triggerButton}
        placement="bottom-start"
        matchTriggerWidth
        className="z-9999"
        contentClassName="p-0 overflow-hidden rounded-2xl md:rounded-3xl"
      >
        {dropdownBody}
      </Popover>

      {/* Helper text / Error message */}
      {(helperText || effectiveError) && (
        <p
          className={cn(
            "text-xs transition-colors duration-200 flex items-center gap-1.5",
            effectiveError ? "text-destructive" : "text-muted-foreground",
          )}
        >
          {effectiveError && (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5 shrink-0">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z"
                clipRule="evenodd"
              />
            </svg>
          )}
          {effectiveError || helperText}
        </p>
      )}
    </div>
  );
};
